import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { KIOSK_HUB_URL } from '../../shared/api/apiConfig';
import { useRealtimeFeedHub } from './useRealtimeFeedHub';

const KIOSK_RECONNECT_DELAYS = [0, 1000, 3000, 5000, 10000, 15000, 30000, 30000, 30000, 30000, 30000, 30000, 30000, 30000];

async function syncRegisterSubscription(hub, previousRegisterId, nextRegisterId) {
  if (!hub || hub.state !== signalR.HubConnectionState.Connected) {
    return previousRegisterId;
  }

  if (previousRegisterId && previousRegisterId !== nextRegisterId) {
    await hub.invoke('LeaveRegister', previousRegisterId).catch(() => {});
  }

  if (nextRegisterId && previousRegisterId !== nextRegisterId) {
    await hub.invoke('JoinRegister', nextRegisterId).catch(() => {});
  }

  return nextRegisterId || '';
}

export function useKioskHub({
  registerId,
  onRequestUpdated,
  onReadingReceived,
  onLoadUpdated,
  onReconnected,
  onConnectionStateChange,
} = {}) {
  const hubRef = useRef(null);
  const joinedRegisterRef = useRef('');
  const registerIdRef = useRef(registerId ?? '');

  useEffect(() => {
    registerIdRef.current = registerId ?? '';
  }, [registerId]);

  const hub = useRealtimeFeedHub(KIOSK_HUB_URL, {
    enabled: import.meta.env.MODE !== 'test',
    events: [
      { name: 'WeighingRequestUpdated', handler: onRequestUpdated },
      { name: 'KioskReadingReceived', handler: onReadingReceived },
      { name: 'RequestLoadUpdated', handler: onLoadUpdated },
    ],
    hubRef,
    onConnectionStateChange,
    onConnected: async (activeHub) => {
      joinedRegisterRef.current = await syncRegisterSubscription(activeHub, '', registerIdRef.current);
    },
    onReconnected: async (activeHub) => {
      joinedRegisterRef.current = '';
      joinedRegisterRef.current = await syncRegisterSubscription(activeHub, '', registerIdRef.current);
      onReconnected?.();
    },
    reconnectDelays: KIOSK_RECONNECT_DELAYS,
    logLabel: 'kiosk hub',
  });

  useEffect(() => {
    const hub = hubRef.current;
    if (!hub || hub.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    let active = true;

    async function updateSubscription() {
      const nextJoinedRegister = await syncRegisterSubscription(
        hub,
        joinedRegisterRef.current,
        registerId ?? '',
      );

      if (active) {
        joinedRegisterRef.current = nextJoinedRegister;
      }
    }

    updateSubscription();

    return () => {
      active = false;
    };
  }, [registerId]);

  return hub;
}
